/* import __COLOCATED_TEMPLATE__ from './conversation-explore-panel.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import { restartableTask } from 'ember-concurrency-decorators';
import { inject as service } from '@ember/service';
import { type TaskGenerator } from 'ember-concurrency';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import { type LogicalFilter } from 'embercom/components/reporting/custom/filters';
import { type FlexibleQueryTimeRange } from 'embercom/services/reporting-csv-export';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import { isPresent } from '@ember/utils';
import type Range from 'embercom/models/reporting/range';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import { buildFiltersForDataConfig } from 'embercom/lib/reporting/custom/data-config-builder-helpers';
import type { ConversationExploreConversation } from 'embercom/components/reporting/automation/ai-insights/conversation-panel';
import { type AttributeMapping } from 'embercom/services/reporting-underlying-data-service';
import { convertMarkdownToHtml } from './summary-converter';

interface Args {
  showSidePanel: boolean;
  onClose: () => void;
  isStandalone: boolean;
  dateRange: Range;
  filters?: LogicalFilter;
  trackAnalyticsEvent: (action: string, object: string, extraContext: Record<string, any>) => void;
  attributeIds: string[];
  attributeValues: string[];
  summaryHeading?: string;
  summaryType?: SummaryTab;
  summaryText?: string;
  metricId?: string;
  selectedConversationId?: number;
  conversationIdsToLoad?: number[];
}

const CONVERSATION_PAGE_SIZE = 100;

export type SummaryTab =
  | 'issue_summaries'
  | 'cxscore_drivers'
  | 'attribute_drivers'
  | 'resolution_rate_drivers';

export default class ConversationExplorePanel extends Component<Args> {
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @service declare appService: AppService;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare intl: IntlService;
  @service declare reportingChartService: ReportingChartService;
  @service declare contentSuggestionsService: ContentSuggestionsService;

  @tracked totalHits = 0;
  @tracked pageFrom = 0;
  @tracked loadedConversations: ConversationExploreConversation[] = [];
  @tracked selectedSummaryTab: SummaryTab = this.args.summaryType || 'issue_summaries';
  @tracked selectedConversationId = this.args.selectedConversationId || 0;
  @tracked openSectionId: string | undefined = undefined;

  fetchDataTask = taskFor(this.loadTopicDrillInData);

  conversationData = trackedTask(this, this.fetchDataTask, () => [
    this.args.attributeIds,
    this.args.attributeValues,
    this.args.dateRange,
    this.pageFrom,
  ]);

  get filterState(): LogicalFilter {
    // ensure the filters are correctly transformed
    let filters = [this.args.filters];
    if (isPresent(this.args.conversationIdsToLoad)) {
      // When opened on the topic trends page, we only want to show outlier conversations
      // so we reset the filter and only include the outlier conversation ids

      filters = [
        {
          type: 'and',
          filters: [
            {
              type: 'category',
              data: {
                attribute: 'conversation.id',
                property: 'conversation_id',
                values: this.args.conversationIdsToLoad || [],
              },
            },
          ],
        },
      ];
    }
    return buildFiltersForDataConfig(
      null, // renderableChart
      this.metric,
      null, // parentMetric
      { type: 'and', filters },
      false, // includeReportLevelFilters
    ) as LogicalFilter;
  }

  get metric() {
    return this.reportingMetrics.getMetricById(this.args.metricId || 'v1.new_conversations');
  }

  get conversationFetchError() {
    return this.fetchDataTask.lastErrored?.error as any;
  }

  get timeRange(): FlexibleQueryTimeRange {
    return {
      start: this.args.dateRange.startMoment.valueOf(),
      end: this.args.dateRange.endMoment.valueOf(),
      interval: this.args.dateRange.interval,
      property: this.metric.timeProperty,
    };
  }

  @restartableTask
  *loadTopicDrillInData(): TaskGenerator<void> {
    if (!this.args.attributeIds || !this.args.dateRange) {
      return;
    }

    if (this.pageFrom === 0) {
      this.loadedConversations = [];
    }

    let filters: LogicalFilter = this.filterState;
    let timeRange = this.timeRange;
    let attributeIds = [
      'conversation.id',
      'conversation.first_user.name',
      'conversation.first_user.pseudonym',
      'conversation_custom_fields#ai_insights_conversation_summary',
      'conversation_custom_fields#ai_insights_subtopic',
      'conversation_custom_fields#ai_insights_topic',
      'conversation.ai_generated_metrics.csat_explanation',
      'conversation.ai_generated_metrics.csat',
    ];
    let sortBy = 'conversation.first_user_conversation_part_created_at';
    let sortDirection: 'asc' | 'desc' = 'desc';
    let syntheticDataUseCase = this.reportingChartService.syntheticDataUseCase;
    let isBeingRenderedAsAChart = false;
    let pageFrom = this.pageFrom;
    let pageSize = CONVERSATION_PAGE_SIZE;
    let result = yield this.reportingUnderlyingDataService.loadDrillInResult(
      this.appService.app.id,
      this.metric.datasetId,
      filters,
      pageFrom,
      pageSize,
      timeRange,
      attributeIds,
      sortBy,
      sortDirection,
      syntheticDataUseCase,
      this.appService.app.timezone,
      isBeingRenderedAsAChart,
    );
    if (isPresent(result.data) && this.pageFrom === 0) {
      let resultsContainSelectedConversation = result.data.some((conversation: any) => {
        return conversation['conversation.id'] === this.selectedConversationId;
      });
      if (!resultsContainSelectedConversation) {
        this.selectedConversationId = result.data[0]['conversation.id'];
      }
    }
    this.totalHits = result.totalHits;

    this.loadedConversations.pushObjects(
      result.data.map(
        (conversation: any) =>
          ({
            id: Number(conversation['conversation.id']),
            userName: conversation['conversation.first_user.name'],
            userPseudonym: conversation['conversation.first_user.pseudonym'],
            topicSummary:
              conversation['conversation_custom_fields#ai_insights_conversation_summary'],
            subtopic: conversation['conversation_custom_fields#ai_insights_subtopic'],
            topic: conversation['conversation_custom_fields#ai_insights_topic'],
            cxExplanation: conversation['conversation.ai_generated_metrics.csat_explanation'],
            cxScore: conversation['conversation.ai_generated_metrics.csat'],
          }) as ConversationExploreConversation,
      ),
    );
  }

  get summaryText(): string {
    return this.args.summaryText || '';
  }

  get availableCxAttributes(): AttributeMapping {
    return this.reportingUnderlyingDataService.getAttributeKnownValues(
      'conversation_custom_fields#customer_experience_attributes',
    );
  }

  get cxAttributeOptions() {
    return Object.entries(this.availableCxAttributes).map(([value, text]) => ({
      value,
      text: text as string,
    }));
  }

  get attributeLabel(): string {
    if (this.args.summaryHeading) {
      return this.args.summaryHeading;
    }

    let value = this.reportingUnderlyingDataService.mappedValueFor(
      this.args.attributeValues[0],
      this.args.attributeIds[0],
    );

    if (typeof value === 'string') {
      return value;
    } else if (value && typeof value === 'object') {
      return value.toString();
    }

    return this.args.attributeValues[0] || '';
  }

  @action
  loadMoreConversations() {
    this.pageFrom += CONVERSATION_PAGE_SIZE;
    this.args.trackAnalyticsEvent('load-more-conversations', 'conversation-explorer', {
      pageFrom: this.pageFrom,
    });
  }

  @action
  onClose() {
    this.args.onClose();
    this.pageFrom = 0;
  }

  @action
  onConversationSelected(conversationId: number) {
    this.selectedConversationId = conversationId;
  }
  get processedSummaryText(): string {
    return convertMarkdownToHtml(this.summaryText, true, true);
  }
  @action
  onConversationLinkClick(conversationId: number) {
    this.onConversationSelected(conversationId);
  }

  @action
  onSummaryTextClick(event: Event) {
    let target = event.target as HTMLElement;
    if (target.tagName === 'A' && target.dataset.conversationId) {
      event.preventDefault();
      let conversationId = parseInt(target.dataset.conversationId, 10);
      this.onConversationLinkClick(conversationId);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::Optimize::ConversationExplorePanel': typeof ConversationExplorePanel;
    'reporting/automation/ai-insights/optimize/conversation-explore-panel': typeof ConversationExplorePanel;
  }
}
